import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, Play, Loader2, Link2, AlertTriangle, Users, Eye } from "lucide-react";

const ETIQUETAS_STATS = {
  placas_completadas: "Placas completadas",               // P41
  clientes_dat_completados: "Cliente de DAT completado",  // P41
  referencias_actualizadas: "Referencias corregidas en DAT",                // P41
  referencias_editadas_a_mano: "Referencias editadas a mano (no se tocan)", // P41
  loads_invalidados_por_reemplazo: "Viajes anulados: DAT reemplazó el ticket",  // P43
  viajes_pasados_a_ticket_nuevo: "Viajes que pasaron al ticket nuevo de DAT",  // P43
  tickets_reemplazados_omitidos: "Tickets reemplazados en DAT (sin viaje)",    // P43
  unidades_operadores_actualizados: "Unidad / placas / operador cambiados en DAT", // P43
  facturas_canceladas_quitadas: "Facturas canceladas en DAT quitadas del viaje",   // P43
  complementos_actualizados: "Complementos actualizados en DAT",                   // P43
  costos_intercompany_anulados: "Costos de Álvarez anulados (CFDI cancelado en DAT)", // P43
  viajes_renumerados: "Viajes que pasaron a la serie de su moneda (T / TN)",          // P44
  tickets_pedidos_por_trafico: "Tickets pedidos por el archivo de tráfico",          // P46
  loads_trafico_asignados: "Viajes con su número de tráfico",                          // P46
  loads_vinculados_nuevos: "Viajes vinculados",
  loads_vinculados_por_ticket_nuevo: "Viajes vinculados (ticket nuevo)",
  loads_actualizados: "Viajes actualizados (factura / cobro)",
  loads_completados: "Viajes con datos completados",
  loads_invalidados: "Viajes invalidados",
  loads_creados: "Viajes creados",
  loads_activos: "Viajes activos revisados",
  loads_sin_link_al_inicio: "Viajes sin vínculo al inicio",
  loads_con_varios_candidatos: "Viajes con varios candidatos",
  cobros_registrados: "Cobros registrados",
  cobros_parciales: "Cobros parciales detectados",
  cruces_creados: "Cruces creados",
  cruces_vinculados: "Cruces vinculados",
  cajas_mov_creados: "Movimientos de caja",
  cajas_mov_ya_existentes: "Movimientos de caja ya existentes",
  clientes_creados: "Clientes creados",
  clientes_dat: "Clientes leídos de DAT",
  clientes_mapeados_nuevos: "Clientes mapeados nuevos",
  unidades_creadas: "Unidades creadas",
  unidades_actualizadas: "Unidades actualizadas",
  unidades_dat: "Unidades leídas de DAT",
  tickets_leidos: "Tickets leídos",
  tickets_previos_sin_viaje: "Tickets previos a la fecha de creación",
  tickets_internos_omitidos: "Tickets internos omitidos",
  tickets_intercompany_sin_viaje: "Tickets intercompany (sin viaje)",
  tickets_cancelados_omitidos: "Tickets cancelados omitidos",
  tickets_sin_cliente: "Tickets sin cliente mapeado",
  tickets_nuevos_no_creados_por_config: "Tickets nuevos no creados (configuración)",
  facturas_actualizadas: "Facturas actualizadas en DAT",
  errores: "Errores",
  // P38 (2026-09-25): viajes de Álvarez desde los tickets intercompany de DAT.
  loads_alvarez_creados: "Viajes de Álvarez creados",
  tickets_alvarez_agrupados: "Tickets de Álvarez agrupados en un viaje existente",
  tickets_alvarez_sin_cliente: "Tickets de Álvarez sin cliente reconocido (se facturan a mano)",
  tickets_alvarez_sin_referencia: "Tickets de Álvarez sin referencia",
  tickets_alvarez_sin_ruta: "Tickets de Álvarez sin ciudades",
  tickets_alvarez_cancelado: "Tickets de Álvarez cancelados",
  tickets_alvarez_sin_aprendizaje: "Tickets de Álvarez (todavía no hay aprendizaje de QuickBooks)",
  clientes_alvarez_creados: "Clientes creados desde QuickBooks",
  clientes_alvarez_mapeados: "Clientes mapeados a QuickBooks (Álvarez)",
  rutas_alvarez_mapeadas: "Rutas mapeadas a items (Álvarez)",
  duracion_seg: "Duración (s)",
};

const ETIQUETAS_ACCION = {
  vincular: "Vincular viaje",
  actualizar_load: "Actualizar factura / cobro",
  completar_load: "Completar datos",
  crear_viaje: "Crear viaje",
  crear_cruce: "Crear cruce",
  vincular_cruce: "Vincular cruce",
  crear_caja_mov: "Crear movimiento de caja",
  crear_cliente: "Crear cliente",
  crear_unidad: "Crear unidad",
  actualizar_unidad: "Actualizar unidad",
  invalidar_load: "Invalidar viaje",
  crear_viaje_alvarez: "Crear viaje de Álvarez",   // P38
  actualizar_referencia: "Referencia corregida en DAT",   // P41
  mover_vinculo: "Pasar el viaje al ticket nuevo de DAT",   // P43
  actualizar_unidad: "Unidad / operador cambiados en DAT",   // P43
  quitar_factura_cancelada: "Quitar factura cancelada en DAT",   // P43
  anular_costo_intercompany: "Anular costo (CFDI cancelado en DAT)",   // P43
};

const ETIQUETAS_PENDIENTE = {
  sin_match: "Sin ticket en DAT",
  match_dudoso: "Match dudoso",
  ticket_repetido: "Ticket ya vinculado",
  cliente_sin_mapear: "Cliente sin mapear",
  factura_cancelada: "Factura cancelada",
  cobro_inconsistente: "Cobro inconsistente",
  monto_distinto: "Monto distinto",
  load_bloqueado: "Viaje bloqueado",
  ticket_cancelado: "Ticket cancelado",
  aviso_factura: "Aviso de factura",
  ticket_reemplazado: "Ticket reemplazado en DAT",   // P43
  costo_cfdi_cancelado: "Costo pagado de un CFDI cancelado",   // P43
};

const TOGGLES = [
  ["crear_viajes", "Crear viajes nuevos desde DAT"],
  ["crear_cruces", "Crear cruces"],
  ["crear_cajas", "Crear movimientos de caja"],
  ["crear_clientes", "Crear clientes que no existen"],
  ["sincronizar_unidades", "Sincronizar unidades"],
  ["monto_manda_dat", "DAT manda en factura y monto"],
  ["completar_fechas", "Completar fechas (delivery / cruce)"],
  ["invalidar_cancelados", "Invalidar viajes de tickets cancelados"],
  // P38 (2026-09-25): apagado por defecto. Además necesita la fecha "Viajes de Álvarez desde".
  ["crear_viajes_alvarez", "Crear viajes de Álvarez (tickets ALVAREZ TRUCKING)"],
  // P41 (2026-10-02): monto, cobro y cliente de los viajes con factura de Álvarez hecha directo en QuickBooks.
  ["conciliar_qbo_alvarez", "QuickBooks manda en los viajes de Álvarez (monto, cobro y cliente)"],
];

const etiquetaStat = (k) => {
  if (ETIQUETAS_STATS[k]) return ETIQUETAS_STATS[k];
  if (k.startsWith("preview_")) return `Vista previa: ${ETIQUETAS_ACCION[k.slice(8)] || k.slice(8)}`;
  if (k.startsWith("pendiente_")) return `Pendiente: ${ETIQUETAS_PENDIENTE[k.slice(10)] || k.slice(10)}`;
  return k;
};

export function SincronizacionDAT() {
  const [estado, setEstado] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ items: [], conteos: {} });
  const [accion, setAccion] = useState("");
  const [pendientes, setPendientes] = useState([]);
  const [tipoPend, setTipoPend] = useState("");
  const [clientesMap, setClientesMap] = useState([]);
  const [soloSinMapear, setSoloSinMapear] = useState(false);
  const [clientesPlataforma, setClientesPlataforma] = useState([]);
  const [dialogo, setDialogo] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [ticketsBusqueda, setTicketsBusqueda] = useState([]);
  const [linkQuery, setLinkQuery] = useState("");
  const [links, setLinks] = useState([]);
  const timer = useRef(null);

  const loadEstado = useCallback(async () => {
    try {
      const { data } = await api.get("/dat/sync/estado");
      setEstado(data);
      setForm((f) => f || { ...data.settings });
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      return null;
    }
  }, []);

  const loadPreview = useCallback(async (acc = "") => {
    try {
      const { data } = await api.get("/dat/sync/preview", { params: { accion: acc || undefined, limite: 400 } });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const loadPendientes = useCallback(async (tipo = "") => {
    try {
      const { data } = await api.get("/dat/sync/pendientes", { params: { tipo: tipo || undefined, estado: "abierto", limite: 200 } });
      setPendientes(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const loadClientesMap = useCallback(async (solo = false) => {
    try {
      const { data } = await api.get("/dat/clientes-map", { params: { solo_sin_mapear: solo } });
      setClientesMap(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  const loadClientesPlataforma = useCallback(async () => {
    try {
      const acc = [];
      for (let skip = 0; skip < 4000; skip += 200) {
        const { data } = await api.get("/clients", { params: { limit: 200, skip } });
        const items = data.items || data.clients || [];
        acc.push(...items);
        if (items.length < 200) break;
      }
      acc.sort((a, b) => (a.razon_social || "").localeCompare(b.razon_social || ""));
      setClientesPlataforma(acc);
    } catch (e) {
      /* el select queda vacío; no bloquea la pantalla */
    }
  }, []);

  useEffect(() => {
    loadEstado();
    loadPreview("");
    loadPendientes("");
    loadClientesMap(false);
    loadClientesPlataforma();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [loadEstado, loadPreview, loadPendientes, loadClientesMap, loadClientesPlataforma]);

  const arrancarPolling = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(async () => {
      const data = await loadEstado();
      if (data && !data.en_curso) {
        clearInterval(timer.current);
        timer.current = null;
        loadPreview(accion);
        loadPendientes(tipoPend);
      }
    }, 3000);
  }, [loadEstado, loadPreview, loadPendientes, accion, tipoPend]);

  useEffect(() => {
    if (estado?.en_curso && !timer.current) arrancarPolling();
  }, [estado, arrancarPolling]);

  const ejecutar = async () => {
    try {
      const { data } = await api.post("/dat/sync/ejecutar");
      toast.success(`Sincronización iniciada en modo ${data.modo === "aplicar" ? "Aplicar" : "Simulación"}`);
      await loadEstado();
      arrancarPolling();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reiniciar = async () => {
    if (!window.confirm("¿Volver a leer todos los tickets y facturas de DAT en la próxima corrida?")) return;
    try {
      await api.post("/dat/sync/reiniciar-watermarks");
      toast.success("La próxima corrida vuelve a leer todo");
      loadEstado();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const guardar = async () => {
    const base = estado?.settings || {};
    const cambios = {};
    Object.keys(form || {}).forEach((k) => {
      if (["watermark_tickets", "watermark_facturas", "ultima_corrida", "clave", "created_at", "updated_at",
           "facturacion_completa_en"].includes(k)) return;
      if (form[k] !== base[k]) cambios[k] = k === "ventana_dias_match" ? Number(form[k]) : form[k];
    });
    if (!Object.keys(cambios).length) { toast.info("No hay cambios para guardar"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/dat/sync/settings", cambios);
      setForm({ ...data });
      toast.success("Configuración guardada");
      loadEstado();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const ignorarPendiente = async (id) => {
    try {
      await api.post(`/dat/sync/pendientes/${id}/ignorar`);
      loadPendientes(tipoPend);
      loadEstado();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const buscarTickets = async () => {
    if (busqueda.trim().length < 3) { toast.info("Escribí al menos 3 caracteres"); return; }
    try {
      const { data } = await api.get("/dat/sync/buscar-tickets", { params: { q: busqueda.trim() } });
      setTicketsBusqueda(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const vincular = async (dat_ticket_id) => {
    try {
      await api.post("/dat/sync/vincular", { load_id: dialogo.datos.load_id, dat_ticket_id });
      toast.success("Viaje vinculado con el ticket de DAT");
      setDialogo(null);
      setTicketsBusqueda([]);
      setBusqueda("");
      loadPendientes(tipoPend);
      loadEstado();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const guardarMapa = async (m) => {
    try {
      await api.put(`/dat/clientes-map/${m.dat_client_id}`, { client_id: m.client_id || "", tipo: m.tipo || "cliente" });
      toast.success("Mapeo guardado");
      loadClientesMap(soloSinMapear);
      loadEstado();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const buscarLinks = async () => {
    try {
      const { data } = await api.get("/dat/sync/links", { params: { q: linkQuery.trim() || undefined } });
      setLinks(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!estado || !form) {
    return (
      <Card className="mb-5"><CardContent className="py-6 text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando sincronización…
      </CardContent></Card>
    );
  }

  const ult = estado.settings?.ultima_corrida;
  const stats = ult?.stats || {};
  const detallePreview = (p) => {
    if (p.accion === "actualizar_load") return (p.cambios || []).map((c) => `${c.campo}: ${c.antes ?? "—"} → ${c.despues ?? "—"}`).join(" · ");
    if (p.accion === "completar_load") return Object.entries(p.cambios || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
    if (p.accion === "vincular") return `${p.motivo || ""} (puntaje ${p.puntaje})`;
    return `${p.ticket?.origen || "—"} → ${p.ticket?.destino || "—"}`;
  };

  return (
    <>
      <Card className="mb-5" data-testid="dat-sync-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading">
            <RefreshCw className="h-5 w-5 text-primary" /> Sincronización con DAT
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${form.modo === "aplicar" ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`} data-testid="dat-sync-modo-badge">
              {form.modo === "aplicar" ? "Aplicar" : "Simulación"}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <div data-testid="dat-sync-vinculados">Viajes vinculados: <b>{estado.viajes_vinculados}</b></div>
            <div data-testid="dat-sync-sin-mapear">Clientes DAT sin mapear: <b>{estado.clientes_sin_mapear}</b></div>
            <div>
              {estado.en_curso ? (
                <span className="flex items-center gap-2 text-primary" data-testid="dat-sync-en-curso">
                  <Loader2 className="h-4 w-4 animate-spin" /> Sincronizando… etapa: {estado.en_curso.etapa}
                </span>
              ) : ult ? (
                <span data-testid="dat-sync-ultima">
                  Última: {ult.inicio?.slice(0, 16).replace("T", " ")} · {ult.modo === "aplicar" ? "Aplicar" : "Simulación"} · {stats.duracion_seg ?? "?"} s ·{" "}
                  {ult.ok ? <b className="text-emerald-700">OK</b> : <b className="text-red-700">Error: {(ult.errores || [])[0]}</b>}
                </span>
              ) : <span className="text-muted-foreground">Sin corridas todavía</span>}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={ejecutar} disabled={!!estado.en_curso} data-testid="dat-sync-run">
              <Play className="h-4 w-4 mr-1" /> Sincronizar ahora
            </Button>
            <Button variant="outline" onClick={reiniciar} data-testid="dat-sync-reset">
              <RefreshCw className="h-4 w-4 mr-1" /> Volver a leer todo
            </Button>
          </div>

          {form.modo === "aplicar" && (
            <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex gap-2" data-testid="dat-sync-aviso-aplicar">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              En modo Aplicar la sincronización escribe de verdad en viajes, cruces, cajas, clientes y unidades. Revisá la vista previa antes.
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label>Modo</Label>
              <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.modo}
                      onChange={(e) => setForm({ ...form, modo: e.target.value })} data-testid="dat-sync-mode">
                <option value="simulacion">Simulación (no escribe)</option>
                <option value="aplicar">Aplicar (escribe)</option>
              </select>
            </div>
            <div>
              <Label>Crear viajes desde</Label>
              <Input type="date" value={form.fecha_inicio_creacion || ""} onChange={(e) => setForm({ ...form, fecha_inicio_creacion: e.target.value })} data-testid="dat-sync-fecha-creacion" />
              <p className="text-xs text-muted-foreground mt-1">Los tickets anteriores solo se vinculan.</p>
            </div>
            <div>
              <Label>Vincular tickets desde</Label>
              <Input type="date" value={form.fecha_inicio_vinculacion || ""} onChange={(e) => setForm({ ...form, fecha_inicio_vinculacion: e.target.value })} data-testid="dat-sync-fecha-vinculacion" />
            </div>
            <div>
              <Label>Ventana de días para el match</Label>
              <Input type="number" min={1} max={90} value={form.ventana_dias_match ?? 20}
                     onChange={(e) => setForm({ ...form, ventana_dias_match: e.target.value })} data-testid="dat-sync-ventana" />
            </div>
            {/* P38 (2026-09-25): sin esta fecha no se crea ningún viaje de Álvarez aunque el interruptor esté prendido. */}
            <div>
              <Label>Viajes de Álvarez desde</Label>
              <Input type="date" value={form.fecha_inicio_viajes_alvarez || ""}
                     onChange={(e) => setForm({ ...form, fecha_inicio_viajes_alvarez: e.target.value || null })}
                     data-testid="dat-sync-fecha-alvarez" />
              <p className="text-xs text-muted-foreground mt-1">Tickets ALVAREZ TRUCKING desde esta fecha.</p>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} data-testid="dat-sync-activo" />
            Sincronizar automáticamente cada 30 minutos
          </label>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {TOGGLES.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`dat-sync-${k}`} />
                {label}
              </label>
            ))}
          </div>

          <Button onClick={guardar} disabled={saving} data-testid="dat-sync-save">Guardar configuración</Button>

          {!!Object.keys(stats).length && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Resultado de la última corrida</h4>
              <Table>
                <TableBody>
                  {Object.entries(stats).map(([k, v]) => (
                    <TableRow key={k}><TableCell className="text-sm">{etiquetaStat(k)}</TableCell><TableCell className="text-sm text-right font-mono">{String(v)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-5">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Eye className="h-5 w-5 text-primary" /> Vista previa de la última simulación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!Object.keys(preview.conteos || {}).length ? (
            <p className="text-sm text-muted-foreground" data-testid="dat-sync-preview-vacio">No hay vista previa. Ejecutá una sincronización en modo simulación.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {Object.entries(preview.conteos).map(([a, n]) => (
                  <button key={a} onClick={() => { setAccion(a); loadPreview(a); }}
                          className={`text-xs px-3 py-1 rounded-full border transition-colors ${accion === a ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                          data-testid={`dat-sync-preview-${a}`}>
                    {ETIQUETAS_ACCION[a] || a}: {n}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Viaje / Ticket</TableHead><TableHead>Cliente</TableHead><TableHead>Referencia</TableHead>
                    <TableHead>Tipo / Sentido</TableHead><TableHead>Monto</TableHead><TableHead>Factura</TableHead><TableHead>Detalle</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(preview.items || []).map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-sm">{p.numero_load || p.ticket?.dat_folio || p.economico || "—"}</TableCell>
                        <TableCell className="text-sm">{p.cliente || p.ticket?.dat_client_nombre || p.razon_social || "—"}</TableCell>
                        <TableCell className="text-sm">{p.referencia_cliente || p.ticket?.load_id_dat || "—"}</TableCell>
                        <TableCell className="text-sm">{p.tipo_viaje || p.sentido || p.tipo || "—"}</TableCell>
                        <TableCell className="text-sm">{p.monto != null ? `${p.monto} ${p.moneda || ""}` : "—"}</TableCell>
                        <TableCell className="text-sm">{p.numero_factura || "—"}</TableCell>
                        <TableCell className="text-sm">{detallePreview(p)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="mb-5">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><AlertTriangle className="h-5 w-5 text-primary" /> Pendientes para revisar</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tipoPend}
                    onChange={(e) => { setTipoPend(e.target.value); loadPendientes(e.target.value); }} data-testid="dat-pend-tipo">
              <option value="">Todos los tipos</option>
              {Object.keys(estado.pendientes_abiertos || {}).map((t) => (
                <option key={t} value={t}>{ETIQUETAS_PENDIENTE[t] || t}</option>
              ))}
            </select>
          </div>
          {!pendientes.length ? (
            <p className="text-sm text-muted-foreground" data-testid="dat-pend-vacio">No hay pendientes abiertos.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Tipo</TableHead><TableHead>Descripción</TableHead><TableHead>Última vez</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {pendientes.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-muted">{ETIQUETAS_PENDIENTE[p.tipo] || p.tipo}</span></TableCell>
                      <TableCell className="text-sm">{p.descripcion}</TableCell>
                      <TableCell className="text-sm">{(p.ultima_vez || "").slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {p.datos?.load_id && (
                          <Button size="sm" variant="outline" className="mr-2" onClick={() => { setDialogo(p); setTicketsBusqueda([]); setBusqueda(p.datos?.referencia_cliente || ""); }} data-testid="dat-pend-vincular">
                            <Link2 className="h-4 w-4 mr-1" /> Vincular…
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => ignorarPendiente(p.id)} data-testid="dat-pend-ignorar">Ignorar</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-5">
        <CardHeader><CardTitle className="flex items-center gap-2 font-heading"><Users className="h-5 w-5 text-primary" /> Clientes DAT ↔ plataforma</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={soloSinMapear} onChange={(e) => { setSoloSinMapear(e.target.checked); loadClientesMap(e.target.checked); }} data-testid="dat-map-solo-sin-mapear" />
            Solo sin mapear
          </label>
          {!clientesMap.length ? (
            <p className="text-sm text-muted-foreground" data-testid="dat-map-vacio">No hay clientes de DAT cargados. Ejecutá una sincronización.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Cliente DAT</TableHead><TableHead>Tipo</TableHead><TableHead>Cliente plataforma</TableHead><TableHead>Origen</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {clientesMap.map((m) => (
                    <TableRow key={m.dat_client_id}>
                      <TableCell className={`text-sm ${m.dat_activo === false ? "text-muted-foreground" : ""}`}>{m.dat_nombre}</TableCell>
                      <TableCell>
                        <select className="h-8 rounded-md border border-input bg-background px-2 text-sm" value={m.tipo || "cliente"}
                                onChange={(e) => setClientesMap(clientesMap.map((x) => x.dat_client_id === m.dat_client_id ? { ...x, tipo: e.target.value } : x))}>
                          <option value="cliente">Cliente</option>
                          <option value="interno">Interno</option>
                          <option value="intercompany">Intercompany</option>
                          <option value="ignorar">Ignorar</option>
                        </select>
                      </TableCell>
                      <TableCell>
                        <select className="h-8 rounded-md border border-input bg-background px-2 text-sm max-w-[240px]" value={m.client_id || ""}
                                onChange={(e) => setClientesMap(clientesMap.map((x) => x.dat_client_id === m.dat_client_id ? { ...x, client_id: e.target.value } : x))}>
                          <option value="">— sin mapear —</option>
                          {clientesPlataforma.map((c) => (
                            <option key={c.id || c._id} value={c.id || c._id}>{c.razon_social}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell className="text-sm">{m.origen || "—"}{m.similitud ? ` (${m.similitud})` : ""}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => guardarMapa(m)} data-testid={`dat-map-save-${m.dat_client_id}`}>Guardar</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="pt-3 border-t space-y-2">
            <h4 className="text-sm font-semibold">Ver vínculo de un viaje</h4>
            <div className="flex gap-2">
              <Input placeholder="Número de viaje, folio o referencia DAT" value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} data-testid="dat-link-find" />
              <Button variant="outline" onClick={buscarLinks}>Buscar</Button>
            </div>
            {links.map((l) => (
              <div key={l.id} className="text-sm rounded-md border p-2">
                <b>{l.numero}</b> · folio DAT {l.dat_folio} · {l.dat_client_nombre} · ref. {l.load_id_dat} · {l.metodo_match} (puntaje {l.puntaje})
                {l.facturacion && (
                  <div className="text-muted-foreground">
                    {l.facturacion.numero_factura} · {l.facturacion.monto} {l.facturacion.moneda} ·{" "}
                    {l.facturacion.cobrada ? `Cobrada el ${l.facturacion.fecha_cobro}` : `Saldo ${l.facturacion.saldo}`}
                    {(l.facturacion.avisos || []).length ? ` · ${l.facturacion.avisos.join(" · ")}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!dialogo} onOpenChange={(o) => { if (!o) { setDialogo(null); setTicketsBusqueda([]); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Vincular {dialogo?.datos?.numero_load} con un ticket de DAT</DialogTitle></DialogHeader>
          {!!dialogo?.datos?.candidatos?.length && (
            <div className="space-y-1">
              <p className="text-sm font-semibold">Candidatos encontrados</p>
              <Table>
                <TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Cliente</TableHead><TableHead>Referencia</TableHead><TableHead>Fecha</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {dialogo.datos.candidatos.map((c) => (
                    <TableRow key={c.dat_ticket_id}>
                      <TableCell className="text-sm">{c.dat_folio}</TableCell>
                      <TableCell className="text-sm">{c.dat_client_nombre}</TableCell>
                      <TableCell className="text-sm">{c.load_id_dat}</TableCell>
                      <TableCell className="text-sm">{(c.start_date || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-right"><Button size="sm" onClick={() => vincular(c.dat_ticket_id)}>Vincular</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="flex gap-2">
            <Input placeholder="Buscar en DAT (mín. 3 caracteres)" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} data-testid="dat-link-search" />
            <Button variant="outline" onClick={buscarTickets}>Buscar</Button>
          </div>
          {!!ticketsBusqueda.length && (
            <div className="overflow-x-auto max-h-72">
              <Table>
                <TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Cliente</TableHead><TableHead>Referencia</TableHead><TableHead>Fecha</TableHead><TableHead>Ruta</TableHead><TableHead>Unidad</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {ticketsBusqueda.map((t) => (
                    <TableRow key={t.dat_ticket_id}>
                      <TableCell className="text-sm">{t.dat_folio}</TableCell>
                      <TableCell className="text-sm">{t.dat_client_nombre}</TableCell>
                      <TableCell className="text-sm">{t.load_id_dat}</TableCell>
                      <TableCell className="text-sm">{(t.start_date || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-sm">{t.origen || "—"} → {t.destino || "—"}</TableCell>
                      <TableCell className="text-sm">{t.unidad || "—"}</TableCell>
                      <TableCell className="text-right"><Button size="sm" onClick={() => vincular(t.dat_ticket_id)}>Vincular</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
